<template>
  <div class="speed-game-container">
    <!-- Modale de reprise du jeu -->
    <div v-if="showResumeModal && savedProgress" class="badge-unlock-overlay">
      <div class="badge-unlock-animation resume-modal">
        <div class="badge-icon">🎮</div>
        <h2>Reprendre ton jeu ?</h2>
        <p class="resume-message">
          Tu as déjà progressé jusqu'au niveau
          <strong>{{ savedProgress.currentLevel + 1 }}/{{ levels.length }}</strong>
        </p>
        <p class="resume-message">
          Veux-tu reprendre là où tu t'es arrêté ?
        </p>
        <div class="resume-actions">
          <button @click="handleResume" class="close-animation-btn resume-btn">
            <span class="btn-icon">▶️</span>
            Reprendre
          </button>
          <button @click="handleRestart" class="close-animation-btn restart-btn">
            <span class="btn-icon">🔄</span>
            Recommencer
          </button>
        </div>
      </div>
    </div>

    <!-- Calibration logic moved to game content -->

    <!-- Animation de déblocage du badge -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <div class="badge-unlock-animation">
        <img :src="badgeSpeedImage" alt="Badge" class="badge-icon" />
        <h2>Badge débloqué !</h2>
        <h3>{{ badgeData.name }}</h3>
        <p>{{ badgeData.description }}</p>
        <button @click="closeBadgeAnimation" class="close-animation-btn">
          Continuer
        </button>
      </div>
    </div>

    <GameGuide
      v-if="!gameStarted && !showResumeModal"
      gameId="speed-game"
      :forceShow="true"
      @start-game="startGame"
      @skip-intro="startGame"
    />

    <!-- Zone de jeu principale -->
    <div class="game-playground" v-if="gameStarted && !showResults" @contextmenu.prevent>
      <!-- Barre de progression -->
      <div class="progress-container">
        <div class="progress-steps">
          <div class="progress-step" :class="{ completed: currentLevel > 0 }">
            <div class="step-icon">🎮</div>
            <div class="step-label">Démarrage</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{
              completed: currentLevel >= Math.floor(levels.length / 3),
            }"
          >
            <div class="step-icon">🏃</div>
            <div class="step-label">En cours</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{
              completed: currentLevel >= Math.floor((levels.length * 2) / 3),
            }"
          >
            <div class="step-icon">🔍</div>
            <div class="step-label">Avancé</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{ completed: currentLevel >= levels.length }"
          >
            <div class="step-icon">🏆</div>
            <div class="step-label">Terminé</div>
          </div>
        </div>

        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: progressPercentage + '%' }"
          ></div>
          <div class="progress-text">
            {{ currentLevel + 1 }} / {{ levels.length }}
          </div>
        </div>
      </div>

      <div class="level-indicator">
        <div class="level-badge">
          Niveau {{ currentLevel + 1 }}: {{ levels[currentLevel].type }}
        </div>
      </div>

      <!-- Contenu du jeu -->
      <div class="game-content">
        <!-- Compte à rebours avant le niveau -->
        <div v-if="countdownActive" class="countdown-overlay">
          <div class="countdown-value">{{ countdownValue }}</div>
        </div>

        <!-- Calibration Question Step -->
        <div v-if="isCalibrating && calibrationStep === 'question'" class="calibration-question-container">
          <!-- Flamou avec bulle de dialogue -->
          <div class="calibration-flamou-section">
            <img :src="flamouImage" alt="Flamou" class="calibration-flamou-image" />
            <div class="calibration-speech-bubble">
              <h2 class="calibration-title">Salut, moi c'est Flamou ! 👋</h2>
              <p class="calibration-flamou-text">
                Avant de commencer, j'aimerais savoir quelque chose pour adapter le jeu à ton niveau...
              </p>
              <p class="calibration-question">
                <strong>Es-tu à l'aise avec un clavier ?</strong>
              </p>
            </div>
          </div>

          <!-- Boutons de réponse en cartes -->
          <div class="comfort-cards-container">
            <div @click="setComfortLevel('comfortable')" class="comfort-card comfortable-card">
              <div class="comfort-card-icon">😊</div>
              <h3 class="comfort-card-title">Oui !</h3>
              <p class="comfort-card-desc">Je tape vite sur un clavier</p>
            </div>

            <div @click="setComfortLevel('somewhat')" class="comfort-card somewhat-card">
              <div class="comfort-card-icon">🤔</div>
              <h3 class="comfort-card-title">Un peu</h3>
              <p class="comfort-card-desc">Je me débrouille, ça dépend</p>
            </div>

            <div @click="setComfortLevel('not-comfortable')" class="comfort-card not-comfortable-card">
              <div class="comfort-card-icon">😅</div>
              <h3 class="comfort-card-title">Pas trop</h3>
              <p class="comfort-card-desc">J'ai besoin de temps</p>
            </div>
          </div>
        </div>

        <div v-else class="typing-section-with-sidebars">
          <!-- Timer à gauche -->
          <div class="timer-sidebar" v-if="!isCalibrating">
            <div class="timer-container">
              <div
                class="circular-timer"
                :class="{ warning: timeLeft <= 10, danger: timeLeft <= 5, critical: timeLeft <= 3 }"
              >
                <div class="timer-glow"></div>
                <svg class="timer-svg" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" class="timer-background" />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    class="timer-progress"
                    :style="{
                      strokeDasharray: `${2 * Math.PI * 42}`,
                      strokeDashoffset: `${2 * Math.PI * 42 * (1 - timeLeft / levelTimeLimit)}`,
                    }"
                  />
                </svg>
                <div class="timer-text">
                  <div class="timer-icon">⏱️</div>
                  <div class="timer-value">{{ timeLeft }}</div>
                  <div class="timer-label">secondes</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Zone de frappe au centre -->
          <div class="typing-section">
            <div class="typing-challenge">
              <div class="typing-target">
                <div class="target-text">
                  <span
                    v-for="(char, index) in (isCalibrating ? calibrationText : targetText)"
                    :key="index"
                    :class="{
                      'correct-char':
                        index < inputValue.length && char === inputValue[index],
                      'incorrect-char':
                        index < inputValue.length && char !== inputValue[index],
                      'current-char': index === inputValue.length,
                      'pending-char': index > inputValue.length,
                    }"
                  >
                    {{ char }}
                  </span>
                </div>
              </div>

              <div class="typing-input-container">
                <input
                  ref="inputField"
                  type="text"
                  class="typing-input"
                  v-model="inputValue"
                  @input="isCalibrating ? checkCalibrationInput($event) : checkInput($event)"
                  :disabled="levelCompleted || countdownActive"
                  placeholder="Tape ici..."
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                />
              </div>
            </div>

            <div
              class="feedback-message"
              :class="feedbackClass"
              v-if="feedback"
            >
              {{ feedback }}
            </div>
          </div>

          <!-- Flamou à droite -->
          <div class="flamou-sidebar">
            <div class="flamou-container" v-if="showFlamous">
              <div class="flamou-character">
                <div class="flamou-bubble">
                  <p>{{ currentFlamouMessage }}</p>
                </div>
                <img
                  :src="currentFlamouImage"
                  alt="Flamou"
                  class="flamou-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats du jeu -->
      <div class="stats-container">
        <div class="stat-item">
          <div class="stat-label">Précision</div>
          <div class="stat-value">{{ accuracy }}%</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">WPM</div>
          <div class="stat-value">{{ wpm }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Erreurs</div>
          <div class="stat-value">{{ mistakes }}</div>
        </div>
      </div>

      <!-- Bouton "Arrêter de jouer" en haut à droite -->
      <!-- <div class="stop-game-container">
        <button @click="endGame" class="stop-game-button">
          <span class="btn-icon">🛑</span>
          <span class="btn-text">Arrêter de jouer</span>
        </button>
      </div> -->

      <!-- Boutons d'action -->
      <div class="game-actions">
        <button
          v-if="levelCompleted"
          @click="nextLevel"
          class="action-button next-button"
        >
          <span class="btn-icon">▶️</span>
          <span class="btn-text">Niveau suivant</span>
        </button>

        <button
          @click="restartLevel"
          class="action-button restart-button"
          v-if="!levelCompleted && !isCalibrating"
        >
          <span class="btn-icon">🔄</span>
          <span class="btn-text">Recommencer</span>
        </button>
      </div>
    </div>

    <!-- Écran des résultats -->
    <div class="results-overlay" v-if="showResults">
      <div class="results-modal">
        <div class="results-header">
          <div class="results-title-container">
            <div class="results-title-icon">🏆</div>
            <h2 class="results-title">Félicitations !</h2>
          </div>
          <p class="results-subtitle">Voici un résumé de ta performance</p>
        </div>

        <div class="results-statistics">
          <div class="stat-item">
            <div class="stat-value">{{ finalScore }}</div>
            <div class="stat-label">Score Total</div>
          </div>

          <div class="stat-item">
            <div class="stat-value">{{ averageWpm }}</div>
            <div class="stat-label">WPM Moyen</div>
          </div>

          <div class="stat-item">
            <div class="stat-value">{{ totalAccuracy }}%</div>
            <div class="stat-label">Précision</div>
          </div>
        </div>

        <p class="result-message">
          {{ getResultMessage() }}
        </p>

        <div class="results-actions">
          <button @click="restartGame" class="action-button restart-button">
            <span class="btn-icon">🔄</span>
            <span class="btn-text">Rejouer</span>
          </button>
          <button @click="recalibrate" class="action-button calibration-button">
            <span class="btn-icon">⚡</span>
            <span class="btn-text">Refaire le test de vitesse</span>
          </button>
          <button @click="goToNextGame" class="action-button next-game-button">
            <span class="btn-icon">🎮</span>
            <span class="btn-text">Prochain jeu</span>
          </button>
          <button @click="goToDashboard" class="action-button home-button">
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Retour à l'accueil</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { unlockBadge, isBadgeUnlocked } from '@/utils/badges'
import GameGuide from '@/components/GameGuideComponent.vue'
import AuthService from '@/services/AuthService'
import flamouImage from '@/assets/flamou/intresting.png'
import flamouThumbsUp from '@/assets/flamou/happy2.png'
import flamouNeutral from '@/assets/flamou/happy.png'
import flamouSad from '@/assets/flamou/sad.png'
import badgeSpeedImage from '@/assets/badges_skills/Badge_jeu_de_vitesse.png'
import GameNavigationService from '@/services/GameNavigationService'

export default {
  name: 'GameSpeed',
  components: {
    GameGuide,
  },
  setup() {
    return {
      flamouImage,
      flamouNeutral,
      flamouThumbsUp,
      flamouSad,
      badgeSpeedImage,
    }
  },

  data() {
    return {
      gameStarted: false,
      showResults: false,
      currentLevel: 0,
      inputValue: '',
      targetText: '',
      timeLeft: 0,
      levelTimeLimit: 0, // Temps limite ajusté pour le niveau actuel
      timer: null,
      levelTimer: null,
      isTypingActive: false,
      lastTypingTime: null,
      typingInactivityTimer: null,
      TYPING_INACTIVITY_DELAY: 3000,
      mistakes: 0,
      wpm: 0,
      accuracy: 100,
      feedback: '',
      feedbackClass: '',
      levelCompleted: false,
      gameStartTime: null,
      levelStartTime: null,
      levelResults: [],
      showBadgeUnlockAnimation: false,
      badgeSpeedMasterId: 1,
      countdownInterval: null,
      totalErrorsTracked: 0,
      errorPositions: new Set(),
      badgeData: {
        name: 'Maître de la vitesse',
        description:
          'Tu as terminé le jeu de vitesse avec une excellente performance !',
      },
      countdownActive: false,
      countdownValue: 3,
      showFlamous: false,
      currentFlamouImage: flamouNeutral,
      currentFlamouMessage: '',
      autoAdvanceTimer: null,
      savedProgress: null,
      hasLoadedProgress: false,
      showResumeModal: false,
      // Système de calibration
      isCalibrating: false,
      calibrationCompleted: false,
      calibrationStep: 'question', // 'question' ou 'test'
      keyboardComfortLevel: null, // 'comfortable', 'somewhat', 'not-comfortable'
      timeMultiplier: 1,
      calibrationStartTime: null,
      calibrationText: 'Le chat dort sur le canapé.',
      flamouMessages: {
        encouragement: [
          'Tu te débrouilles très bien !',
          'Continue comme ça !',
          'Je crois en toi !',
          'Tu peux y arriver !',
        ],
        success: [
          'Excellent travail !',
          "Bravo, c'est parfait !",
          'Tu es vraiment doué !',
          'Fantastique !',
        ],
        struggle: [
          'Prends ton temps, pas de pression !',
          'Tu y es presque !',
          "N'abandonne pas !",
          'Chaque erreur est une leçon !',
        ],
      },
      // Structure des niveaux avec une difficulté progressive
      levels: [
        // Un mot court unique pour commencer facilement
        {
          type: 'Mot court',
          text: 'chat',
          timeLimit: 5,
        },
        // Un mot long unique
        {
          type: 'Mot long',
          text: 'informatique',
          timeLimit: 10,
        },
        // Une phrase très courte
        {
          type: 'Phrase courte',
          text: 'Le chat dort.',
          timeLimit: 10,
        },
        // Une phrase un peu plus longue
        {
          type: 'Phrase moyenne',
          text: "Il fait beau aujourd'hui.",
          timeLimit: 12,
        },
        // Quelques mots courts
        {
          type: 'Mots courts',
          text: 'chat chien arbre pomme soleil',
          timeLimit: 15,
        },
        // Quelques mots longs
        {
          type: 'Mots longs',
          text: 'développement technologie communication',
          timeLimit: 20,
        },
        // Phrase plus complexe
        {
          type: 'Phrase complexe',
          text: 'Les nouvelles technologies évoluent rapidement dans notre monde.',
          timeLimit: 25,
        },
        // Un petit paragraphe pour finir
        {
          type: 'Paragraphe',
          text: "La vitesse de frappe est une compétence utile. Elle permet de gagner du temps et d'être plus efficace. Avec de la pratique, on peut tous s'améliorer.",
          timeLimit: 30,
        },
      ],
    }
  },
  computed: {
    // Calcul du pourcentage de progression dans les niveaux
    progressPercentage() {
      return (this.currentLevel / this.levels.length) * 100
    },

    // Mise en forme du texte cible avec coloration des caractères tapés
    formattedTargetText() {
      if (!this.targetText) return ''

      const typedLength = this.inputValue.length
      let result = ''

      for (let i = 0; i < this.targetText.length; i++) {
        if (i < typedLength) {
          // Caractère déjà tapé
          if (this.inputValue[i] === this.targetText[i]) {
            // Caractère correct - vert
            result += `<span class="correct-char">${this.targetText[i]}</span>`
          } else {
            // Caractère incorrect - rouge
            result += `<span class="incorrect-char">${this.targetText[i]}</span>`
          }
        } else if (i === typedLength) {
          // Position actuelle du curseur - souligné et en surbrillance
          result += `<span class="current-char">${this.targetText[i]}</span>`
        } else {
          // Caractère pas encore tapé - normal
          result += `<span class="pending-char">${this.targetText[i]}</span>`
        }
      }

      return result
    },

    // Résultats finaux pour l'écran de résultat
    finalScore() {
      // Calcul du score total en fonction du WPM moyen et de la précision
      return Math.round(this.averageWpm * (this.totalAccuracy / 100))
    },

    averageWpm() {
      if (this.levelResults.length === 0) return 0

      const totalWpm = this.levelResults.reduce(
        (sum, result) => sum + result.wpm,
        0,
      )
      return Math.round(totalWpm / this.levelResults.length)
    },

    totalAccuracy() {
      if (this.levelResults.length === 0) return 100

      const totalAccuracy = this.levelResults.reduce(
        (sum, result) => sum + result.accuracy,
        0,
      )
      return Math.round(totalAccuracy / this.levelResults.length)
    },
  },
  beforeUnmount() {
    // Nettoyer les timers avant de quitter la page
    this.clearTimers()
  },
  methods: {
    // Charger la progression sauvegardée depuis le backend ou localStorage
    async loadSavedProgress() {
      try {
        const response = await AuthService.request('get', '/games/speed')
        if (response.data && typeof response.data === 'object') {
          let data = response.data

          // Harmoniser les noms de propriétés - gérer current_level et currentLevel
          let currentLevel = data.currentLevel !== undefined
            ? data.currentLevel
            : data.current_level

          // Convertir en nombre si nécessaire
          if (typeof currentLevel === 'string') {
            currentLevel = parseInt(currentLevel)
          }

          // Si currentLevel n'est pas disponible ou est 0, essayer de le calculer depuis levelStats
          if ((!currentLevel || currentLevel === 0) && data.levelStats) {
            const levelKeys = Object.keys(data.levelStats)
            if (levelKeys.length > 0) {
              // Trouver le niveau le plus élevé dans levelStats
              const maxLevel = Math.max(...levelKeys.map(k => parseInt(k)))
              currentLevel = maxLevel
            }
          }

          // Vérifier que les données sont valides
          if (currentLevel && currentLevel > 0) {
            this.savedProgress = {
              currentLevel: currentLevel,
              completion: data.completion || 0,
              levelStats: data.levelStats || {},
            }
            
            // Extract calibration data if available
            if (data.keyboardComfortLevel) {
              this.keyboardComfortLevel = data.keyboardComfortLevel
              this.calibrationCompleted = true
            }
            if (data.timeMultiplier) {
              this.timeMultiplier = data.timeMultiplier
            }

            return this.savedProgress
          }
        }
      } catch (error) {

        // Fallback sur localStorage
        const localData = JSON.parse(
          localStorage.getItem('speedGameStats') || '{}'
        )

        // Harmoniser les noms de propriétés
        let currentLevel = localData.currentLevel !== undefined
          ? localData.currentLevel
          : localData.current_level

        if (typeof currentLevel === 'string') {
          currentLevel = parseInt(currentLevel)
        }

        // Si currentLevel n'est pas disponible ou est 0, essayer de le calculer depuis levelStats
        if ((!currentLevel || currentLevel === 0) && localData.levelStats) {
          const levelKeys = Object.keys(localData.levelStats)
          if (levelKeys.length > 0) {
            // Trouver le niveau le plus élevé dans levelStats
            const maxLevel = Math.max(...levelKeys.map(k => parseInt(k)))
            currentLevel = maxLevel
          }
        }

        if (currentLevel && currentLevel > 0) {
          this.savedProgress = {
            currentLevel: currentLevel,
            completion: localData.completion || 0,
            levelStats: localData.levelStats || {},
          }
          return this.savedProgress
        }
      }

      return null
    },

    // Reprendre le jeu depuis la progression sauvegardée
    resumeFromProgress() {
      if (!this.savedProgress) {
        return
      }

      // Charger le niveau sauvegardé
      // savedProgress.currentLevel = nombre de niveaux complétés = index du prochain niveau
      this.currentLevel = Math.min(
        this.savedProgress.currentLevel,
        this.levels.length - 1
      )

      // Reconstruire les résultats des niveaux précédents si disponibles
      if (this.savedProgress.levelStats) {
        Object.keys(this.savedProgress.levelStats).forEach(levelKey => {
          const levelNum = parseInt(levelKey)
          const stats = this.savedProgress.levelStats[levelKey]

          if (levelNum <= this.currentLevel) {
            this.levelResults.push({
              level: levelNum,
              wpm: stats.wpm || 0,
              accuracy: stats.accuracy || 0,
              mistakes: stats.errors || 0,
              time: 0, // On ne sauvegarde pas le temps
            })
          }
        })
      }

      this.hasLoadedProgress = true
    },

    saveCurrentLevelStats(isSuccess) {
      // D'abord, récupérer les données existantes
      AuthService.request('get', '/games/speed')
        .then((response) => {
          // Initialiser la structure si les données sont invalides
          let currentData =
            response.data && typeof response.data === 'object'
              ? response.data
              : { currentLevel: 0, completion: 0, levelStats: {} }

          // S'assurer que la structure minimale est présente et que les valeurs sont valides
          if (!currentData.levelStats) {
            currentData.levelStats = {}
          }

          // Harmoniser les noms de propriétés - utiliser currentLevel du backend s'il existe
          if (currentData.current_level !== undefined) {
            currentData.currentLevel = currentData.current_level
            delete currentData.current_level // Supprimer l'ancienne propriété
          }

          // S'assurer que currentLevel est un nombre valide
          if (
            typeof currentData.currentLevel !== 'number' ||
            isNaN(currentData.currentLevel)
          ) {
            currentData.currentLevel = 0
          }

          // Mettre à jour le niveau actuel si le niveau complété est plus élevé
          const newLevel = this.currentLevel + 1

          if (newLevel > currentData.currentLevel) {
            currentData.currentLevel = newLevel
          }

          // Calculer le pourcentage de complétion avec validation
          const levelsLength =
            this.levels && this.levels.length ? this.levels.length : 1
          const currentLevel = currentData.currentLevel || 0
          const completionPercentage = (currentLevel / levelsLength) * 100

          // S'assurer que le résultat est un nombre valide
          currentData.completion = isNaN(completionPercentage)
            ? 0
            : parseFloat(completionPercentage.toFixed(1)) / 100

          // Ajouter les statistiques du niveau actuel
          const levelKey = String(this.currentLevel + 1) // Les niveaux commencent à 1 dans le JSON

          currentData.levelStats[levelKey] = {
            success: isSuccess,
            wpm: this.wpm || 0,
            accuracy: this.accuracy || 0,
            errors: this.mistakes || 0,
          }

          // Nettoyer l'objet pour n'envoyer que les propriétés nécessaires
          // Envoyer dans les deux formats pour compatibilité backend
          const dataToSend = {
            currentLevel: currentData.currentLevel,
            current_level: currentData.currentLevel, // Format snake_case pour le backend
            completion: currentData.completion,
            levelStats: currentData.levelStats,
            keyboardComfortLevel: this.keyboardComfortLevel || "somewhat",
            timeMultiplier: this.timeMultiplier || 1.0,
          }


          // Envoyer les données mises à jour
          return AuthService.request('post', '/games/speed', dataToSend)
        })
        .catch((error) => {
          console.error(
            'Erreur lors de la mise à jour des statistiques:',
            error,
          )

          if (error.response) {
            console.error("Réponse d'erreur du serveur:", {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            })
          }

          // En cas d'erreur d'authentification, sauvegarder localement
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            console.warn(
              "Problème d'authentification. Vos statistiques seront sauvegardées localement.",
            )
            this.saveStatsLocally(isSuccess)
          }
        })
    },

    saveStatsLocally(isSuccess) {
      // Récupérer les données existantes du localStorage
      let localData = JSON.parse(
        localStorage.getItem('speedGameStats') ||
          '{"currentLevel":0,"completion":0,"levelStats":{}}',
      )

      // S'assurer que la structure minimale est présente
      if (!localData.levelStats) {
        localData.levelStats = {}
      }

      // Harmoniser les noms de propriétés si nécessaire
      if (localData.current_level !== undefined) {
        localData.currentLevel = localData.current_level
        delete localData.current_level
      }

      // S'assurer que currentLevel est un nombre valide
      if (
        typeof localData.currentLevel !== 'number' ||
        isNaN(localData.currentLevel)
      ) {
        localData.currentLevel = 0
      }

      // Mettre à jour le niveau actuel si le niveau complété est plus élevé
      const newLevel = this.currentLevel + 1
      if (newLevel > localData.currentLevel) {
        localData.currentLevel = newLevel
      }

      // Calculer le pourcentage de complétion avec validation
      const levelsLength =
        this.levels && this.levels.length ? this.levels.length : 1
      const currentLevel = localData.currentLevel || 0
      const completionPercentage = (currentLevel / levelsLength) * 100

      // S'assurer que le résultat est un nombre valide
      localData.completion = isNaN(completionPercentage)
        ? 0
        : parseFloat(completionPercentage.toFixed(1))

      // Ajouter les statistiques du niveau actuel
      const levelKey = String(this.currentLevel + 1) // Les niveaux commencent à 1 dans le JSON

      localData.levelStats[levelKey] = {
        success: isSuccess,
        wpm: this.wpm || 0,
        accuracy: this.accuracy || 0,
        errors: this.mistakes || 0,
      }

      // Nettoyer l'objet pour ne garder que les propriétés nécessaires
      // Garder les deux formats pour compatibilité
      const dataToSave = {
        currentLevel: localData.currentLevel,
        current_level: localData.currentLevel,
        completion: localData.completion,
        levelStats: localData.levelStats,
      }

      // Sauvegarder dans localStorage
      localStorage.setItem('speedGameStats', JSON.stringify(dataToSave))
    },

    // Démarrer le jeu
    async startGame() {
      // Vérifier d'abord la calibration
      if (!this.calibrationCompleted) {
        const hasCalibration = await this.checkForCalibration()
        if (!hasCalibration) {
          // Démarrer la calibration dans le jeu
          this.isCalibrating = true
          this.calibrationStep = 'question'
          this.gameStarted = true
          return
        }
      }

      // Charger la progression sauvegardée si ce n'est pas déjà fait
      if (!this.hasLoadedProgress) {
        await this.loadSavedProgress()

        // Si une progression existe et que le jeu n'est pas terminé, proposer de reprendre
        if (this.savedProgress && this.savedProgress.currentLevel > 0 &&
            this.savedProgress.currentLevel < this.levels.length) {

          // Afficher la modale de reprise
          this.showResumeModal = true
          this.hasLoadedProgress = true
          return // Attendre la réponse de l'utilisateur
        }

        this.hasLoadedProgress = true
      }

      this.gameStarted = true
      this.gameStartTime = new Date()

      this.loadLevel()
    },

    // Gérer la reprise du jeu
    handleResume() {
      this.showResumeModal = false

      // Nettoyer tous les timers avant de reprendre
      this.clearTimers()

      // Réinitialiser l'état du compte à rebours
      this.countdownActive = false
      this.countdownValue = 3

      this.resumeFromProgress()
      this.gameStarted = true
      this.gameStartTime = new Date()

      this.loadLevel()
    },

    // Gérer le recommencement du jeu
    handleRestart() {
      this.showResumeModal = false

      // Nettoyer tous les timers avant de recommencer
      this.clearTimers()

      // Réinitialiser complètement l'état du jeu
      this.currentLevel = 0
      this.levelResults = []
      this.savedProgress = null
      this.countdownActive = false
      this.countdownValue = 3
      this.levelCompleted = false
      this.inputValue = ''
      this.showFlamous = false

      this.gameStarted = true
      this.gameStartTime = new Date()

      this.loadLevel()
    },

    // Charger un niveau
    loadLevel() {
      if (this.currentLevel >= this.levels.length) {
        this.endGame()
        return
      }

      this.targetText = this.levels[this.currentLevel].text

      // Appliquer le multiplicateur de temps basé sur la calibration
      const baseTimeLimit = this.levels[this.currentLevel].timeLimit
      this.levelTimeLimit = Math.ceil(baseTimeLimit * this.timeMultiplier)
      this.timeLeft = this.levelTimeLimit


      this.inputValue = ''
      this.mistakes = 0
      this.totalErrorsTracked = 0 // Réinitialiser le compteur total d'erreurs
      this.errorPositions = new Set() // Réinitialiser les positions d'erreurs
      this.wpm = 0
      this.accuracy = 100
      this.feedback = ''
      this.feedbackClass = ''
      this.levelCompleted = false

      // Démarrer le compte à rebours avant de commencer le niveau
      this.startCountdown()
    },

    // Démarrer le compte à rebours de 3 secondes
    startCountdown() {

      // S'assurer que le timer du niveau n'est pas déjà en cours
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }

      this.countdownActive = true
      this.countdownValue = 3

      // Nettoyer un éventuel intervalle existant
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }

      this.countdownInterval = setInterval(() => {
        this.countdownValue--

        if (this.countdownValue <= 0) {
          clearInterval(this.countdownInterval)
          this.countdownInterval = null
          this.countdownActive = false


          // Une fois le compte à rebours terminé, ALORS démarrer le niveau et le timer
          this.levelStartTime = new Date()
          this.startLevelTimer()

          // Mettre le focus sur le champ de saisie
          this.$nextTick(() => {
            if (this.$refs.inputField) {
              this.$refs.inputField.focus()
            }
          })
        }
      }, 1000)
    },

    // Démarrer le timer du niveau
    startLevelTimer() {

      // Ne nettoyer que le timer du niveau, pas le compte à rebours qui est déjà terminé
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.levelTimer) {
        clearInterval(this.levelTimer)
        this.levelTimer = null
      }

      this.timer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--
        } else {
          // Le temps est écoulé
          this.clearTimers()
          if (!this.levelCompleted) {
            this.showTimesUpFeedback()
          }
        }
      }, 1000)
    },

    // Vérifier l'entrée de l'utilisateur
    checkInput() {
      this.isTypingActive = true
      this.lastTypingTime = Date.now()

      const input = this.inputValue
      const target = this.targetText

      // Vérifier si le niveau est déjà complété
      if (this.levelCompleted) return

      // Pour suivre les nouvelles erreurs découvertes dans cette frappe
      let newErrorsFound = 0

      // Vérifier chaque caractère tapé
      for (let i = 0; i < input.length; i++) {
        if (i >= target.length || input[i] !== target[i]) {
          // Si cette position n'a pas déjà été marquée comme erreur
          if (!this.errorPositions.has(i)) {
            this.errorPositions.add(i)
            newErrorsFound++
          }
        }
      }

      // Mettre à jour le nombre total d'erreurs de frappe
      this.totalErrorsTracked += newErrorsFound

      // Utiliser le total des erreurs détectées pour les statistiques
      this.mistakes = this.totalErrorsTracked

      // Calculer la précision basée sur le nombre total d'erreurs par rapport aux caractères tapés
      const totalCharactersAttempted = Math.max(
        input.length,
        this.errorPositions.size,
      )
      const accuracy =
        totalCharactersAttempted > 0
          ? Math.max(
              0,
              Math.round(
                ((totalCharactersAttempted - this.totalErrorsTracked) /
                  totalCharactersAttempted) *
                  100,
              ),
            )
          : 100
      this.accuracy = accuracy

      // Calculer le WPM (mots par minute)
      const timeElapsed = (new Date() - this.levelStartTime) / 1000 / 60
      const wordsTyped = input.length / 5 // Considère qu'un mot est en moyenne 5 caractères
      this.wpm = timeElapsed > 0 ? Math.round(wordsTyped / timeElapsed) : 0

      // Afficher Flamou selon la performance
      if (this.mistakes > 5 && this.accuracy < 80) {
        // Si beaucoup d'erreurs, montrer Flamou triste
        if (!this.showFlamous) {
          this.showFlamouStruggle()
        }
      } else if (input.length > target.length * 0.7 && this.accuracy > 90) {
        // Si près de la fin avec bonne précision, montrer encouragement
        if (!this.showFlamous) {
          this.showFlamouEncouragement()
        }
      }

      // Forcer la mise à jour de la vue
      this.$nextTick(() => {
        this.inputValue = this.inputValue.slice()
      })

      // Vérifier si le niveau est complété
      if (input === target) {
        this.completeLevel()
      }
    },

    // Compléter le niveau actuel
    completeLevel() {
      this.levelCompleted = true
      this.clearTimers()

      // Enregistrer les résultats du niveau
      this.levelResults.push({
        level: this.currentLevel + 1,
        wpm: this.wpm,
        accuracy: this.accuracy,
        mistakes: this.mistakes,
        time: this.levels[this.currentLevel].timeLimit - this.timeLeft, // temps utilisé
      })

      // Afficher un feedback positif
      this.feedback = 'Excellent ! Niveau complété !'
      this.feedbackClass = 'feedback-correct'

      // Afficher Flamou avec message de succès
      this.showFlamouSuccess()

      // Sauvegarder les statistiques avec succès
      this.saveCurrentLevelStats(true)

      // Auto-advance après 3 secondes
      this.startAutoAdvance()
    },

    // Afficher un message quand le temps est écoulé
    showTimesUpFeedback() {
      this.feedback = 'Temps écoulé !'
      this.feedbackClass = 'feedback-incorrect'

      // Enregistrer les résultats incomplets
      this.levelResults.push({
        level: this.currentLevel + 1,
        wpm: this.wpm,
        accuracy: this.accuracy,
        mistakes: this.mistakes,
        time: this.levels[this.currentLevel].timeLimit, // temps maximal utilisé
      })

      // Sauvegarder les statistiques avec échec
      this.saveCurrentLevelStats(false)

      // Permettre de passer au niveau suivant même si celui-ci n'est pas complété
      this.levelCompleted = true
    },

    // Passer au niveau suivant
    nextLevel() {
      // Annuler l'auto-advance si l'utilisateur clique manuellement
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }

      this.currentLevel++
      this.loadLevel()
    },

    // Recommencer le niveau actuel
    restartLevel() {
      this.loadLevel()
    },

    // Terminer le jeu et afficher les résultats
    endGame() {
      this.clearTimers()

      // Calculer la complétion avec validation
      const levelsLength =
        this.levels && this.levels.length ? this.levels.length : 1
      const currentLevel = this.currentLevel + 1
      const completionPercentage = (currentLevel / levelsLength) * 100

      // Sauvegarder les statistiques finales du jeu complet
      const finalStats = {
        currentLevel: currentLevel,
        completion: isNaN(completionPercentage)
          ? 0
          : parseFloat(completionPercentage.toFixed(1)),
        levelStats: {},
      }

      // Convertir les résultats des niveaux en statistiques finales
      this.levelResults.forEach((result) => {
        finalStats.levelStats[result.level] = {
          success: result.accuracy >= 70, // Considéré comme réussi si précision >= 70%
          wpm: result.wpm || 0,
          accuracy: result.accuracy || 0,
          errors: result.mistakes || 0,
        }
      })

      this.showResults = true

      this.unlockSpeedMasterBadge()
    },

    // Redémarrer tout le jeu
    restartGame() {
      this.currentLevel = 0
      this.levelResults = []
      this.showResults = false
      this.gameStartTime = new Date()
      this.savedProgress = null
      this.hasLoadedProgress = true // Empêcher le rechargement de la progression
      this.loadLevel()
    },

    // Nettoyer les timers
    clearTimers() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.levelTimer) {
        clearInterval(this.levelTimer)
        this.levelTimer = null
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }
    },

    // Démarrer l'auto-advance après 3 secondes
    startAutoAdvance() {
      if (this.currentLevel < this.levels.length - 1) {
        this.autoAdvanceTimer = setTimeout(() => {
          this.nextLevel()
        }, 3000)
      }
    },

    // Afficher Flamou avec message de succès
    showFlamouSuccess() {
      this.currentFlamouImage = flamouThumbsUp
      this.currentFlamouMessage =
        this.flamouMessages.success[
          Math.floor(Math.random() * this.flamouMessages.success.length)
        ]
      this.showFlamous = true

      // Cacher Flamou après 4 secondes
      setTimeout(() => {
        this.showFlamous = false
      }, 4000)
    },

    // Afficher Flamou avec message d'encouragement
    showFlamouEncouragement() {
      this.currentFlamouImage = flamouNeutral
      this.currentFlamouMessage =
        this.flamouMessages.encouragement[
          Math.floor(Math.random() * this.flamouMessages.encouragement.length)
        ]
      this.showFlamous = true

      // Cacher Flamou après 3 secondes
      setTimeout(() => {
        this.showFlamous = false
      }, 3000)
    },

    // Afficher Flamou avec message d'aide
    showFlamouStruggle() {
      this.currentFlamouImage = flamouSad
      this.currentFlamouMessage =
        this.flamouMessages.struggle[
          Math.floor(Math.random() * this.flamouMessages.struggle.length)
        ]
      this.showFlamous = true

      // Cacher Flamou après 3 secondes
      setTimeout(() => {
        this.showFlamous = false
      }, 3000)
    },

    // Aller au jeu suivant
    goToNextGame() {
      GameNavigationService.navigateToNextGame(this.$router, '/games/speed')
    },

    // Retourner au tableau de bord
    goToDashboard() {
      GameNavigationService.returnToDashboard(this.$router)
    },

    // Débloquer le badge de maître de la vitesse
    async unlockSpeedMasterBadge() {
      try {
        const isUnlocked = await isBadgeUnlocked(this.badgeSpeedMasterId)
        if (!isUnlocked) {
          const badgeUnlocked = await unlockBadge(this.badgeSpeedMasterId)
          if (badgeUnlocked) {
            setTimeout(() => {
              this.showBadgeUnlockAnimation = true
            }, 500)
          }
        }
      } catch (error) {
        console.error('Erreur lors du déblocage du badge Speed Master:', error)
      }
    },

    // Fermer l'animation du badge
    closeBadgeAnimation() {
      this.showBadgeUnlockAnimation = false
    },

    // Obtenir un message personnalisé selon la performance
    getResultMessage() {
      const accuracy = this.totalAccuracy
      const wpm = this.averageWpm

      if (accuracy >= 95 && wpm >= 40) {
        return 'Impressionnant ! Ta vitesse et ta précision sont exceptionnelles. Tu as un vrai talent pour la frappe rapide !'
      } else if (accuracy >= 90 && wpm >= 30) {
        return 'Très bonne performance ! Tu as une excellente combinaison de vitesse et de précision.'
      } else if (accuracy >= 80 && wpm >= 20) {
        return 'Bon travail ! Avec un peu plus de pratique, tu pourras encore améliorer ta vitesse tout en maintenant une bonne précision.'
      } else if (accuracy >= 70) {
        return "Tu te débrouilles bien ! Continue à t'entraîner pour améliorer ta vitesse et ta précision."
      } else {
        return "C'est un bon début ! La vitesse de frappe s'améliore avec la pratique régulière. N'hésite pas à réessayer !"
      }
    },

    // Méthodes de calibration
    async checkForCalibration() {
      try {
        // Charger la calibration depuis le backend (partie intégrante des données du jeu)
        const response = await AuthService.request('get', '/games/speed')
        // On vérifie keyboardComfortLevel car timeMultiplier a une valeur par défaut de 1.0 au backend
        if (response.data && response.data.keyboardComfortLevel && response.data.keyboardComfortLevel !== "") {
          this.timeMultiplier = response.data.timeMultiplier || 1.0
          this.keyboardComfortLevel = response.data.keyboardComfortLevel
          this.calibrationCompleted = true
          return true
        }
      } catch (error) {
        // Fallback to localStorage if backend fetch fails
      }

      // Fallback sur localStorage
      const localCalibration = localStorage.getItem('speedGameCalibration')
      if (localCalibration) {
        try {
          const calibrationData = JSON.parse(localCalibration)
          if (calibrationData && calibrationData.keyboardComfortLevel) {
            this.timeMultiplier = calibrationData.timeMultiplier || 1
            this.keyboardComfortLevel = calibrationData.keyboardComfortLevel
            this.calibrationCompleted = true
            return true
          }
        } catch (e) {
          console.error('Erreur parsing calibration localStorage', e)
        }
      }

      // Aucune calibration trouvée
      return false
    },

    setComfortLevel(level) {
      this.keyboardComfortLevel = level

      // Passer à l'étape du test
      this.calibrationStep = 'test'
      
      // Afficher les instructions via Flamou
      this.showFlamous = true
      this.currentFlamouMessage = "Maintenant, tape cette phrase à ton rythme. Pas de stress !"
      this.currentFlamouImage = this.flamouNeutral

      // Mettre le focus sur le champ de saisie
      this.$nextTick(() => {
        if (this.$refs.inputField) {
          this.$refs.inputField.focus()
        }
      })
    },

    startCalibration() {
      this.isCalibrating = true
      this.inputValue = ''
      this.calibrationStartTime = null // Ne pas démarrer le chrono tout de suite

      // Focus sur le champ de saisie
      this.$nextTick(() => {
        if (this.$refs.calibrationInput) {
          this.$refs.calibrationInput.focus()
        }
      })
    },

    checkCalibrationInput() {
      const input = this.inputValue
      const target = this.calibrationText

      // Démarrer le chronomètre à la première frappe
      if (input.length === 1 && !this.calibrationStartTime) {
        this.calibrationStartTime = new Date()
      }

      // Vérifier si la calibration est complétée
      if (input === target) {
        this.completeCalibration()
      }
    },

    completeCalibration() {
      const endTime = new Date()
      const timeElapsed = (endTime - this.calibrationStartTime) / 1000 // en secondes


      // Calculer le multiplicateur basé sur le temps
      // Temps de référence : 10 secondes pour cette phrase
      const referenceTime = 10
      let multiplier = timeElapsed / referenceTime

      // Limiter le multiplicateur entre 0.5 et 3
      // 0.5 = très rapide (timers réduits de moitié)
      // 3 = très lent (timers triplés)
      multiplier = Math.max(0.5, Math.min(3, multiplier))

      this.timeMultiplier = multiplier
      this.calibrationCompleted = true


      // Sauvegarder la calibration
      this.saveCalibration()

      // Afficher un message de confirmation
      this.showCalibrationResult()
    },

    showCalibrationResult() {
      // Réinitialiser le champ de saisie
      this.inputValue = ''
      
      // Marquer la calibration comme terminée
      this.isCalibrating = false
      
      // Cacher Flamou après avoir montré le message
      this.showFlamous = false;

      // Démarrer le jeu directement après la calibration
      this.proceedAfterCalibration()
    },

    async saveCalibration() {
      try {
        // D'abord, récupérer les données existantes pour ne pas les écraser
        const response = await AuthService.request('get', '/games/speed')
        let currentData =
          response.data && typeof response.data === 'object'
            ? response.data
            : { currentLevel: 0, completion: 0, levelStats: {} }

        const dataToSend = {
          currentLevel: currentData.currentLevel || 0,
          current_level: currentData.currentLevel || 0,
          completion: currentData.completion || 0,
          levelStats: currentData.levelStats || {},
          timeMultiplier: this.timeMultiplier,
          keyboardComfortLevel: this.keyboardComfortLevel,
        }

        // Sauvegarder sur le backend
        await AuthService.request('post', '/games/speed', dataToSend)
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la calibration:', error)
      }

      // Toujours sauvegarder localement comme fallback
      const localData = {
        timeMultiplier: this.timeMultiplier,
        keyboardComfortLevel: this.keyboardComfortLevel,
        calibratedAt: new Date().toISOString(),
      }
      localStorage.setItem('speedGameCalibration', JSON.stringify(localData))
    },



    proceedAfterCalibration() {
      // Continuer avec le flux normal du jeu
      if (this.savedProgress && this.savedProgress.currentLevel > 0 &&
          this.savedProgress.currentLevel < this.levels.length) {
        this.showResumeModal = true
      } else {
        this.gameStarted = true
        this.gameStartTime = new Date()
        this.loadLevel()
      }
    },

    async recalibrate() {
      // Fermer l'écran de résultats si ouvert
      this.showResults = false

      // Réinitialiser l'état du jeu
      this.gameStarted = true
      this.currentLevel = 0
      this.levelResults = []

      // Réinitialiser la calibration
      this.calibrationCompleted = false
      this.calibrationStep = 'question'
      this.keyboardComfortLevel = null
      this.timeMultiplier = 1
      this.isCalibrating = true
      this.inputValue = ''

      // Supprimer la calibration sauvegardée localement
      localStorage.removeItem('speedGameCalibration')
    },
  },
}
</script>

<style scoped>
/* Base Styles */
.speed-game-container {
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  max-width: 1100px;
  margin: 0 auto;
  padding: 1vh 1vw;
  color: #333;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Game Header */
.game-header {
  text-align: center;
  margin-bottom: 30px;
}

.main-title {
  font-size: 2.5rem;
  color: #2196f3; /* Bleu pour le thème du jeu de vitesse */
  margin-bottom: 10px;
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.main-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background-color: #ff9800;
  border-radius: 2px;
}

.subtitle {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
}

/* Guide Character */
.guide-character {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.guide-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid #ffc107;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.speech-bubble {
  position: relative;
  background-color: #fff;
  border-radius: 15px;
  padding: 15px;
  margin-left: 15px;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
  max-width: 70%;
}

.speech-bubble:before {
  content: '';
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 10px 10px 10px 0;
  border-style: solid;
  border-color: transparent #fff transparent transparent;
}

.speech-bubble p {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  line-height: 1.5;
}

/* Animation du badge débloqué */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: #fff;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 0 30px rgba(33, 150, 243, 0.6); /* Couleur bleue pour le thème du jeu */
  animation: scaleIn 0.5s ease-out;
}

.badge-unlock-animation .badge-icon {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px auto;
  display: block;
  animation: pulse 2s infinite;
}

.badge-unlock-animation h2 {
  color: #2196f3;
  font-size: 2rem;
  margin-bottom: 10px;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 20px;
}

.close-animation-btn {
  background-color: #2196f3;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-animation-btn:hover {
  background-color: #1976d2;
  transform: scale(1.05);
}

/* Modale de reprise */
.resume-modal {
  max-width: 500px;
}

.resume-message {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 15px;
  line-height: 1.5;
}

.resume-message strong {
  color: #2196f3;
  font-size: 1.3rem;
}

.resume-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
}

.resume-actions button {
  flex: 1;
  max-width: 200px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.resume-btn {
  background-color: #4caf50;
}

.resume-btn:hover {
  background-color: #388e3c;
}

.restart-btn {
  background-color: #ff9800;
}

.restart-btn:hover {
  background-color: #f57c00;
}

/* === NOUVEAU DESIGN CALIBRATION AVEC FLAMOU === */

/* Wrapper (integrated) */
.calibration-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

/* Container principal */
.calibration-container {
  background: white;
  border-radius: 30px;
  padding: 40px;
  max-width: 800px;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: scaleIn 0.5s ease-out;
  border: 1px solid #e0e0e0;
}

/* === ÉTAPE 1: QUESTION === */
.calibration-question-step {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Section Flamou */
.calibration-flamou-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.calibration-flamou-image {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid #ffc107;
  box-shadow: 0 4px 10px rgba(255, 193, 7, 0.3);
  flex-shrink: 0;
  animation: floatUp 2s ease-in-out infinite;
}

.calibration-speech-bubble {
  flex: 1;
  background: linear-gradient(135deg, #fff, #f8f9fa);
  border-radius: 15px;
  padding: 15px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffc107;
}

.calibration-speech-bubble::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 25px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 10px 10px 0;
  border-color: transparent #ffc107 transparent transparent;
}

.calibration-speech-bubble::after {
  content: '';
  position: absolute;
  left: -7px;
  top: 27px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 8px 8px 8px 0;
  border-color: transparent #fff transparent transparent;
}

.calibration-title {
  color: #2196f3;
  font-size: 1.2rem;
  margin: 0 0 5px 0;
  font-weight: bold;
}

.calibration-flamou-text {
  color: #555;
  font-size: 0.9rem;
  line-height: 1.3;
  margin: 0 0 8px 0;
}

.calibration-question {
  color: #333;
  font-size: 1rem;
  margin: 0;
  text-align: center;
  padding: 8px;
  background: #e3f2fd;
  border-radius: 6px;
}

/* Cartes de réponse */
.comfort-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
}

.comfort-card {
  background: white;
  border-radius: 8px;
  padding: 8px 5px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.comfort-card:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.comfortable-card {
  border-color: #4caf50;
  background: linear-gradient(135deg, #ffffff, #e8f5e9);
}

.comfortable-card:hover {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.somewhat-card {
  border-color: #ff9800;
  background: linear-gradient(135deg, #ffffff, #fff3e0);
}

.somewhat-card:hover {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
}

.not-comfortable-card {
  border-color: #2196f3;
  background: linear-gradient(135deg, #ffffff, #e3f2fd);
}

.not-comfortable-card:hover {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
}

.comfort-card-icon {
  font-size: 2rem;
  margin-bottom: 5px;
  display: block;
}

.comfort-card-title {
  font-size: 1rem;
  font-weight: bold;
  margin: 0 0 3px 0;
  color: #333;
}

.comfort-card-desc {
  font-size: 0.75rem;
  color: #666;
  margin: 0;
  line-height: 1.2;
}

/* === ÉTAPE 2: TEST === */
.calibration-test-step {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.calibration-test-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
}

.calibration-text-card {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border: 3px solid #2196f3;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  width: 100%;
  box-shadow: 0 4px 15px rgba(33, 150, 243, 0.2);
}

.calibration-text-icon {
  font-size: 3rem;
  margin-bottom: 15px;
}

.calibration-preview-text {
  font-size: 1.3rem;
  color: #1976d2;
  font-weight: bold;
  margin: 0;
  line-height: 1.6;
}

.calibration-start-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 18px 40px;
  background: linear-gradient(135deg, #4caf50, #66bb6a);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.3rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.calibration-start-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.5);
  background: linear-gradient(135deg, #388e3c, #4caf50);
}

/* Zone de frappe */
.calibration-typing-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.calibration-typing-area {
  background: #f8f9fa;
  border-radius: 20px;
  padding: 25px;
  border: 3px solid #e0e0e0;
}

.calibration-typing-target {
  min-height: 60px;
  max-height: none;
  margin-bottom: 20px;
  background: white;
  border: 2px solid #2196f3;
}

.calibration-input {
  border: 3px solid #2196f3;
  font-size: 1.2rem;
}

/* Message de succès */
.calibration-success {
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  border: 3px solid #4caf50;
  border-radius: 15px;
  padding: 20px;
  animation: bounceIn 0.6s ease;
}

.calibration-success-flamou {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid #4caf50;
  flex-shrink: 0;
  animation: pulse 2s infinite;
}

.calibration-success-bubble {
  flex: 1;
  text-align: center;
}

.calibration-success-icon {
  font-size: 2rem;
  margin-bottom: 5px;
}

.calibration-success-title {
  color: #4caf50;
  font-size: 1.3rem;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.calibration-success-text {
  color: #2e7d32;
  font-size: 1rem;
  margin: 0;
  line-height: 1.4;
}

/* Bouton de calibration (pour résultats) */
.calibration-button {
  background-color: #ffc107;
}

.calibration-button:hover {
  background-color: #ffa000;
}

/* Welcome Screen */
.welcome-screen {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  align-items: center;
  margin: 40px 0;
}

.welcome-card {
  flex: 1;
  min-width: 300px;
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.card-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  color: #2196f3;
}

.welcome-card h2 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
}

.instructions-list {
  text-align: left;
  padding-left: 10px;
  margin-bottom: 30px;
}

.instructions-list li {
  margin-bottom: 15px;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 15px;
}

.instruction-step {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: #2196f3;
  color: white;
  border-radius: 50%;
  flex-shrink: 0;
}

.start-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-top: 20px;
  width: auto;
  height: auto;
}

.start-button:hover {
  background-color: #388e3c;
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

.btn-icon {
  font-size: 1.3rem;
}

/* Game Playground */
.game-playground {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1vh 0;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  overflow: hidden;
  min-height: 0;
}

/* Progress Steps */
.progress-container {
  width: 100%;
  max-width: 800px;
  margin-bottom: 1vh;
  flex-shrink: 0;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-icon {
  width: 40px;
  height: 40px;
  background-color: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 5px;
  border: 2px solid #ced4da;
  position: relative;
}

.step-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.progress-step.completed .step-icon {
  background-color: #2196f3;
  color: white;
  border-color: #2196f3;
}

.progress-step.completed .step-label {
  color: #2196f3;
}

.progress-connector {
  height: 2px;
  background-color: #ced4da;
  flex-grow: 1;
  margin: 0 -5px;
  position: relative;
  top: -22px;
  z-index: 1;
}

.progress-bar {
  width: 100%;
  height: 16px;
  background-color: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2196f3, #03a9f4);
  border-radius: 8px;
  transition: width 0.5s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  font-weight: bold;
  font-size: 0.75rem;
}

/* Level Indicator */
.level-indicator {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 1vh;
  flex-shrink: 0;
}

.level-badge {
  background-color: #2196f3;
  color: white;
  padding: 0.5vh 2vw;
  border-radius: 50px;
  font-weight: bold;
  font-size: clamp(0.8rem, 1.5vw, 1rem);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* Countdown Overlay */
.countdown-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  z-index: 10;
}

.countdown-value {
  font-size: 8rem;
  font-weight: bold;
  color: white;
  animation: pulseCountdown 1s infinite;
  text-shadow: 0 0 20px rgba(33, 150, 243, 0.8);
}

@keyframes pulseCountdown {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Game Content */
.game-content {
  position: relative;
  width: 100%;
  max-width: 1000px;
  background-color: white;
  border-radius: 20px;
  padding: 2vh 2vw;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 1vh;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.typing-section-with-sidebars {
  display: flex;
  align-items: flex-start;
  gap: 2vw;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.timer-sidebar {
  flex: 0 0 min(180px, 20vw);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 1vh;
  flex-shrink: 0;
}

.typing-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2vh;
  overflow: hidden;
  min-height: 0;
}

.flamou-sidebar {
  flex: 0 0 min(120px, 15vw);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 1vh;
  flex-shrink: 0;
}

.typing-challenge {
  display: flex;
  flex-direction: column;
  gap: 2vh;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.typing-target {
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 2vh 2vw;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
  min-height: 80px;
  word-wrap: break-word;
  text-align: left;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 45vh;
  border: 2px solid #e0e0e0;
  position: relative;
}

/* Scrollbar styling for better visibility */
.typing-target::-webkit-scrollbar {
  width: 8px;
}

.typing-target::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 4px;
}

.typing-target::-webkit-scrollbar-thumb {
  background: #2196f3;
  border-radius: 4px;
}

.typing-target::-webkit-scrollbar-thumb:hover {
  background: #1976d2;
}

.target-text {
  letter-spacing: 1px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.typing-input-container {
  width: 100%;
}

.typing-input {
  width: 100%;
  padding: 15px;
  font-size: 1.1rem;
  border: 2px solid #ddd;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.typing-input:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 10px rgba(33, 150, 243, 0.3);
}

.typing-input:disabled {
  background-color: #f8f8f8;
  cursor: not-allowed;
}

/* Characters styling */
.correct-char {
  color: #4caf50;
  font-weight: bold;
  background-color: rgba(76, 175, 80, 0.1);
  border-radius: 2px;
  padding: 0 2px;
}

.incorrect-char {
  color: #f44336;
  font-weight: bold;
  background-color: rgba(244, 67, 54, 0.1);
  text-decoration: underline wavy #f44336;
  border-radius: 2px;
  padding: 0 2px;
}

.current-char {
  background-color: #e3f2fd;
  border-bottom: 2px solid #2196f3;
  animation: blink 1s infinite;
  padding: 0 2px;
  border-radius: 2px;
  font-weight: bold;
}

.pending-char {
  color: #666;
  padding: 0 2px;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Feedback Message */
.feedback-message {
  padding: 15px;
  border-radius: 10px;
  font-weight: bold;
  text-align: center;
  animation: fadeIn 0.5s ease;
}

.feedback-correct {
  background-color: #e8f5e9;
  color: #4caf50;
}

.feedback-incorrect {
  background-color: #ffebee;
  color: #f44336;
}

/* Stats Container */
.stats-container {
  display: flex;
  justify-content: space-around;
  width: 100%;
  max-width: 800px;
  /* margin-bottom: 20px; */
  margin-top: 10px;
}

.stat-item {
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 15px;
  text-align: center;
  min-width: 100px;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 1.3rem;
  font-weight: bold;
  color: #2196f3;
}

/* Game Actions */
.game-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 25px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.action-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

.next-button {
  background-color: #4caf50;
}

.next-button:hover {
  background-color: #388e3c;
}

.restart-button {
  background-color: #ff9800;
}

.restart-button:hover {
  background-color: #f57c00;
}

.end-button {
  background-color: #f44336;
}

.end-button:hover {
  background-color: #d32f2f;
}

/* Circular Timer */
.timer-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.circular-timer {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  border-radius: 50%;
  box-shadow:
    0 8px 16px rgba(0, 0, 0, 0.15),
    0 0 0 8px rgba(33, 150, 243, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.timer-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.circular-timer.warning {
  box-shadow:
    0 8px 16px rgba(255, 193, 7, 0.3),
    0 0 0 8px rgba(255, 193, 7, 0.2),
    inset 0 2px 4px rgba(255, 255, 255, 0.8);
}

.circular-timer.warning .timer-glow {
  opacity: 1;
  box-shadow: 0 0 20px rgba(255, 193, 7, 0.4);
}

.circular-timer.danger {
  animation: shake 0.5s infinite;
  box-shadow:
    0 8px 16px rgba(244, 67, 54, 0.4),
    0 0 0 8px rgba(244, 67, 54, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.8);
}

.circular-timer.danger .timer-glow {
  opacity: 1;
  box-shadow: 0 0 30px rgba(244, 67, 54, 0.6);
}

.circular-timer.critical {
  animation: criticalPulse 0.5s infinite;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}

@keyframes criticalPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 8px 16px rgba(244, 67, 54, 0.4),
      0 0 0 8px rgba(244, 67, 54, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow:
      0 12px 24px rgba(244, 67, 54, 0.6),
      0 0 0 12px rgba(244, 67, 54, 0.5);
  }
}

.timer-svg {
  position: absolute;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.timer-background {
  fill: none;
  stroke: #e8e8e8;
  stroke-width: 8;
}

.timer-progress {
  fill: none;
  stroke: #4caf50;
  stroke-width: 8;
  stroke-linecap: round;
  transition:
    stroke-dashoffset 0.5s ease,
    stroke 0.3s ease;
  filter: drop-shadow(0 0 3px rgba(76, 175, 80, 0.5));
}

.circular-timer.warning .timer-progress {
  stroke: #ffc107;
  filter: drop-shadow(0 0 5px rgba(255, 193, 7, 0.6));
}

.circular-timer.danger .timer-progress {
  stroke: #f44336;
  filter: drop-shadow(0 0 8px rgba(244, 67, 54, 0.8));
}

.timer-text {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #333;
  font-weight: bold;
  z-index: 1;
}

.timer-icon {
  font-size: 1.8rem;
  margin-bottom: 2px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.timer-value {
  font-size: 2.5rem;
  line-height: 1;
  font-weight: 900;
  color: #2196f3;
  text-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
  transition: all 0.3s ease;
}

.timer-label {
  font-size: 0.55rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 1.5px;
  font-weight: 600;
}

.circular-timer.warning .timer-value {
  color: #ff9800;
  text-shadow: 0 2px 4px rgba(255, 152, 0, 0.3);
}

.circular-timer.warning .timer-label {
  color: #ff9800;
}

.circular-timer.danger .timer-value {
  color: #f44336;
  text-shadow: 0 2px 4px rgba(244, 67, 54, 0.4);
  animation: numberPulse 0.5s infinite;
}

.circular-timer.danger .timer-label {
  color: #f44336;
}

@keyframes numberPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Stop Game Button */
.stop-game-container {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
}

.stop-game-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.stop-game-button:hover {
  background-color: #d32f2f;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.stop-game-button .btn-icon {
  font-size: 1rem;
}

.stop-game-button .btn-text {
  font-size: 0.9rem;
}

/* Flamou Character */
.flamou-container {
  position: relative;
  z-index: 200;
  animation: bounceIn 0.5s ease;
}

.flamou-character {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.flamou-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid #ffc107;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  animation: floatUp 2s ease-in-out infinite;
}

.flamou-bubble {
  position: relative;
  background-color: #fff;
  border-radius: 20px;
  padding: 10px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid #ffc107;
  max-width: 150px;
  margin-bottom: 10px;
}

.flamou-bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #ffc107;
}

.flamou-bubble::before {
  content: '';
  position: absolute;
  top: 98%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #fff;
}

.flamou-bubble p {
  margin: 0;
  font-size: 0.8rem;
  color: #333;
  font-weight: bold;
  line-height: 1.3;
  text-align: center;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.3) translateY(100px);
    opacity: 0;
  }
  50% {
    transform: scale(1.1) translateY(-10px);
    opacity: 1;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes floatUp {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* Results Overlay */
.results-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.5s ease-out;
}

.results-modal {
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.5s ease-out;
}

.results-header {
  text-align: center;
  margin-bottom: 30px;
}

.results-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}

.results-title-icon {
  font-size: 2.5rem;
  color: #2196f3;
}

.results-title {
  font-size: 2rem;
  color: #333;
  margin: 0;
}

.results-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
}

/* Results Statistics */
.results-statistics {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
}

.results-statistics .stat-item {
  width: 30%;
  padding: 20px;
}

.results-statistics .stat-value {
  font-size: 2rem;
}

.result-message {
  text-align: center;
  font-size: 1.1rem;
  color: #555;
  margin: 25px 0;
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 10px;
  line-height: 1.6;
}

.results-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 30px;
}

.next-game-button {
  background-color: #9c27b0;
}

.next-game-button:hover {
  background-color: #7b1fa2;
}

.home-button {
  background-color: #607d8b;
}

.home-button:hover {
  background-color: #455a64;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }

  .game-content {
    padding: 15px;
  }

  .typing-section-with-sidebars {
    flex-direction: column;
    gap: 15px;
  }

  .timer-sidebar,
  .flamou-sidebar {
    flex: none;
    padding-top: 0;
    width: 100%;
    justify-content: center;
  }

  .timer-sidebar {
    order: -1;
  }

  .typing-target {
    font-size: 1rem;
    padding: 15px;
    max-height: 50vh;
  }

  .typing-input {
    font-size: 1rem;
    padding: 12px;
  }

  .results-statistics {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }

  .results-statistics .stat-item {
    width: 80%;
  }

  .game-actions {
    flex-direction: column;
    align-items: center;
  }

  .action-button {
    width: 100%;
    max-width: 300px;
  }

  .resume-actions {
    flex-direction: column;
  }

  .resume-actions button {
    max-width: 100%;
    width: 100%;
  }

  /* Responsive badge animation */
  .badge-unlock-animation {
    padding: 25px;
    max-width: 85%;
    margin: 0 15px;
  }

  .badge-unlock-animation .badge-icon {
    width: 120px;
    height: 120px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.75rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation p {
    font-size: 1rem;
  }

  .close-animation-btn {
    padding: 10px 20px;
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .badge-unlock-animation {
    padding: 20px 15px;
    max-width: 90%;
    margin: 0 10px;
  }

  .badge-unlock-animation .badge-icon {
    width: 100px;
    height: 100px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.5rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.2rem;
  }

  .badge-unlock-animation p {
    font-size: 0.95rem;
  }

  .close-animation-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .level-indicator {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }

  .stop-game-container {
    position: static;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }

  .stop-game-button {
    width: 200px;
  }

  .flamou-container {
    position: relative;
  }

  .flamou-image {
    width: 60px;
    height: 60px;
  }

  .flamou-bubble {
    max-width: 150px;
    font-size: 0.8rem;
  }

  .stats-container {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }

  .stat-item {
    width: 80%;
  }

  .progress-steps {
    display: none;
  }

  .guide-character {
    flex-direction: column;
    text-align: center;
  }

  .speech-bubble {
    margin-left: 0;
    margin-top: 15px;
    max-width: 100%;
  }

  .speech-bubble:before {
    display: none;
  }

  .circular-timer {
    width: 100px;
    height: 100px;
  }

  .timer-value {
    font-size: 1.8rem;
  }

  .timer-icon {
    font-size: 1.3rem;
  }

  .timer-label {
    font-size: 0.6rem;
  }

  .flamou-container {
    position: relative;
  }

  .flamou-image {
    width: 50px;
    height: 50px;
  }

  .flamou-bubble {
    max-width: 120px;
    padding: 8px 12px;
  }

  .flamou-bubble p {
    font-size: 0.75rem;
  }

  .typing-section-with-sidebars {
    flex-direction: column;
    gap: 10px;
  }

  .timer-sidebar,
  .flamou-sidebar {
    flex: none;
    padding-top: 0;
  }

  .typing-target {
    max-height: 55vh;
    font-size: 0.95rem;
    padding: 12px;
  }

  .badge-unlock-animation {
    padding: 15px 10px;
    max-width: 95%;
  }

  .badge-unlock-animation .badge-icon {
    width: 80px;
    height: 80px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.1rem;
  }

  .badge-unlock-animation p {
    font-size: 0.9rem;
  }

  .close-animation-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}
</style>
